/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { Consts, I18nService, TimeService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { type HyperMetroPair } from 'app/common/types/mo';

import { INVALID_NUM } from '../../../../../common/constants';

export enum SpeedTypeE {
    Low = '1',
    Middle = '2',
    High = '3',
    Asap = '4',
    Custom = '5'
}

/**
 * 0：用户自定义 1：根据业务I/O判断
 */
 export enum SiteArbitrationTypeE {
    UserDefined = '1',
    SetIo = '2'
}

@Injectable()
export class HyperMetroPairConst {
    constructor(
        private i18n: I18nService,
        private time: TimeService,
        private util: UtilService,
    ) { }

    DEBUG = false;

    i18nLabel = {
        selectOneAtLeastMsg: this.i18n.get('protection.replication_term_selectOneAtLeast_validation'),
        prefixMsg: this.i18n.get('protection.replication_term_prefix_validation'),
        suffixMsg: this.i18n.get('protection.replication_term_suffix_validation'),
        commonNameErr: this.i18n.get('common.nameformat_validation', [this.i18n.get('common.name_column')]),
        createPair: this.get('protection.hyperMetro_task_createPair_title'),
        remoteResourceNameSame: this.i18n.get('protection.replication_term_remoteResourceNameSame_info_msg'),
    };

    common = {
        remoteVstoreName: this.get('common.remoteVstore_lab'),
        remoteVstoreId: this.get('common.remoteVstoreID_column'),
        owningDrStar: this.i18n.get('protection.dataProtection_term_drStar_ID_lab'),
        autoRescoveryStrategyTips: this.get('protection.HyperMetro_create_setProperty_option_rescoveryStrategyForHA_auto_des_lab'),
        manuRescoveryStrategyTips: this.get('protection.HyperMetro_create_setProperty_option_rescoveryStrategyForHA_manual_des_lab'),

        autoInitalSyncTips: this.get('protection.HyperMetro_HA_task_create_setProperties_info_initiateSync1_tip'),
        manuInitalTips: this.get('protection.HyperMetro_HA_task_createPair_setProperties_info_initiateSync2_tip'),
        noInitalSyncAutoTips: this.get('protection.HyperMetro_HA_task_createPair_setProperties_info_initiateSync_tip'),

        capacityLab: this.get('common.capacity_column'),
        createBtn: this.get('common.create_button'),
        expandBtn: this.get('common.expand_label'),
        modifyBtn: this.get('common.modify_button'),
        deleteBtn: this.get('common.delete_button'),
        confirmBtn: this.get('common.confirm_button'),
        closeBtn: this.get('common.close_button'),
        cancelBtn: this.get('common.cancel_button'),
        advancedLab: this.get('common.advance_label'),
        previousBtn: this.get('common.previous_button'),
        nextBtn: this.get('common.next_button'),
        synBtn: this.get('protection.replicationCG_sync_button'),
        pauseBtn: this.get('protection.hyperMetroCG_term_pause_button'),
        prioritySwitchBth: this.get('protection.hyperMetroCG_term_switchPriority_button'),
        forceStartBtn: this.get('protection.hyperMetroCG_term_forceStart_button'),
        siteArbitrationforceStartBtn: this.get('protection.hyperMetroCG_term_setArbitPolicy_button'),
        siteArbitrationLab: this.get('protection.hyperMetroCG_term_arbitPolicy_col'),
        // 名称
        nameLab: this.get('common.name_column'),
        // wwn
        wwn: this.get('storage.lun_view_summary_localWWN_label'),
        // 描述
        descriptionLab: this.get('common.description_column'),
        // 健康状态
        healthStatusLab: this.get('common.healthStatus_column'),
        // 运行状态
        runningStatusLab: this.get('common.runningStatus_column'),
        // 请选择
        pleaseSelect: this.get('common.select_lab'),
        configure: this.get('protection.replicationCG_task_create_remoteRepliPair_configure_lab'),
        automatic: this.get('common.EnumDefine_STARTUP_MODE_E_AUTO'),
        manually: this.get('common.EnumDefine_STARTUP_MODE_E_MANUAL'),
        host: this.get('common.host_title'),
        hostGroup: this.get('common.hostGroup_title'),
        hyperMetroDomain: this.get('protection.hyperMetroCG_task_create_domain_lab'),
        hyperMetroCg: this.get('protection.hyperMetroCG_title'),
        quorumServer: this.get('common.EnumDefine_HC_CP_TYPE_HC_CP_SERVER'),
        remoteDevice: this.get('common.remoteDevice_title'),
        localResourceLab: this.get('common.localResource_column'),
        remoteResourceLab: this.get('common.remoteResource_column'),
        // None
        none: this.get('common.term_confirm_none'),
        // Action
        actionLab: this.get('common.operation_button'),
        enableLab: this.get('common.enable_label'),
        disableLab: this.get('common.disable_label'),
        loading: this.get('common.loading_label'),
        more: this.get('common.more_button'),
        lunGroup: this.get('common.lunGroup_title'),
        noData: this.i18n.get('monitor.recentTasks_result_noData'),
        confirmLable: this.get('common.confirm_lab'),

        speedRange: this.get('common.valueRange_label', [1, 1024]),
        isolationthResholdRange: this.get('common.valueRange_label', [10, 30000]),
        // 业务保障策略
        serviceAssPolicyLab: this.get('protection.hyperMetro_task_createPair_serviceAssPolicy_lab'),
        // 业务连续优先
        servicePrefValue: this.get('protection.hyperMetro_task_createPair_serviceAssPolicy_servicePref_value'),
        // 数据可靠优先
        dataPrefValue: this.get('protection.hyperMetro_task_createPair_serviceAssPolicy_dataPref_value'),
        remoteNameTip: this.get('protection.replication_term_remoteResourceName_tip', [255, 255]),
        // 业务保障策略冒泡提示
        serviceAssPolicyTip: this.get('protection.hyperMetro_task_createPair_serviceAssPolicy_tip'),
        // 业务保障策略未选择提示
        serviceAssPolicyInfo: this.get('protection.hyperMetro_task_createPair_serviceAssPolicy_selectOne_info'),

        vstoreName: this.get('vstore_name_lab'),
        vstoreId: this.get('vstore_id_lab'),
    };

    create = {
        pairArrowRight: '➜',
        avaiableLunLab: this.get('common.availableLun_column'),
        selectedLab: this.get('common.selectedLun_column'),
        filterDataTip: this.get('common.filterNoMeetData_tip'),
        selectedLuns: this.get('common.selectedLun_column'),
        selectedLun: this.get('common.selected', [this.get('common.objectType_LUN')]),
        selectOneObjectErr: this.get('common.selectOneObject_lab'),
        selectMaxObjectErr: this.get('common.selectedObjectNum_max_tip'),

        configureProtection: this.get('protection.hyperMetroCG_task_create_step1_configureProtection_lab'),
        configureMapping: this.get('protection.hyperMetroCG_task_create_step2_configureMapping_lab'),
        createTitle: this.get('protection.hyperMetro_task_createPair_title'),
        pairCreationMode: this.get('protection.replicationCG_task_create_pairCreateMode_lab'),
        targetStoragePool: this.get('protection.replicaiton_task_create_remoteStoragePool_lab'),
        hyperMetroPairs: this.get('protection.hyperMetroCG_task_create_hyperMetroPair_label'),
        speed: this.get('common.speed_label'),
        speedTip: this.i18n.get('protection.replication_create_task_speed_tip'),
        recoveryPolicy: this.get('protection.replicationCG_term_rescoveryStrategy_lab'),
        initialSync: this.get('protection.replicationCG_task_create_initialSync_lab'),
        inconsistent: this.get('protection.hyperMetroCG_task_create_initialSync_dataInConsistent_checkbox'),
        consistent: this.get('protection.hyperMetroCG_task_create_initialSync_dataConsistent_checkbox'),
        notSync: this.get('protection.hyperMetroCG_task_create_initialSync_dataConsistent_noSync_value'),
        local: this.get('common.EnumDefine_PRIORITY_STATION_TYPE_HyperMetro_LOCAL'),
        remote: this.get('common.EnumDefine_PRIORITY_STATION_TYPE_HyperMetro_REMOTE'),
        lunID: this.get('host.host_task_mappingLun_hostLunID_lab'),
        mappingToSameHosts: this.get('protection.hyperMetroCG_task_create_step2_mappingToSameHosts_checkbox'),
        preferredArray: this.get('protection.hyperMetroCG_task_create_step2_preferredArray_lab'),
        hostConfig: 'Some hosts OS have compatibility risks, Please',
        hostLab: this.get('storage.LunGroup_task_create_mapTo_lab'),
        portGroupLab: this.get('common.portGroup_title'),
        localPortGroupLab: this.get('protection.configuration_remoteDevice_task_addLink_localPortGroup_lab'),
        remotePortGroupLab: this.get('protection.configuration_remoteDevice_task_addLink_remotePortGroup_lab'),
        protection: this.get('common.protection_title'),
        mapping: this.get('common.mapping_column'),
        hosts: this.get('storage.LunGroup_task_create_mapTo_lab'),
        protectionGroup: this.get('protection.protectionGroup_title'),
        pairsErrMsg: this.get('protection.replicaiton_task_exsistLocal_notconfiguredPair_tip'),
        sourceLun: this.get('common.localResource_column'),
        targetLun: this.get('common.remoteResource_column'),
        errorTips: this.get('storage.lunGroup_term_createHyperMetroCG_tip'),
        mappingErr: this.get('protection.hyperMetroCG_task_create_step2_configureMapping_noSupport1_tip'),
        lunMappingTip: this.get('protection.hyperMetroCG_task_create_step2_configureMapping_noSupport2_tip'),
        speedTips: this.get('protection.modifyHyperMetroPair_highestSpeed_confirm_msg'),
        customSpeed: this.get('protection.replicationCG_task_create_custom_speed_lab'),
        specifyObject: this.get('protection.protectionGroup_task_addMember_specifyObject_lab'),
        hyperMetroPairsModifyingTips: this.get('rest.modifyA_hyperMetroPair_msg'),
        hyperMetroPairsModifyTips: (name): string => this.get('rest.modify_hyperMetroPair_msg', [name]),
        /** 自动+普通速率 */
        autoNotHightTip: this.get('protection.dataProctection_activeToActive_task_createHyperMetroPair_confirm_msg'),
        /** 自动+最高速率 */
        autoHightTip: this.get('protection.dataProctection_activeToActive_task_createHyperMetroPair_selectLUN_confirm_msg'),
        /** 手动+普通速率+不同步 */
        manualNotHightConsistentTip: this.get('protection.dataProctection_activeToActive_task_create_errSource_info_createPair_msg'),
        /** 手动+普通速率+自动同步/手动同步 */
        manualNotHightInconsistentTip: this.get('protection.dataProctection_activeToActive_task_createHyperMetro_info_errSource_msg2'),
        /** 手动+最高速率+不同步 */
        manualHighConsistentTip: this.get('protection.dataProctection_activeToActive_task_createHyperMetro_info_forsy_msg'),
        /** 手动+最高速率+自动同步/手动同步 */
        manualHighInconsistentTip: this.get('protection.dataProctection_activeToActive_task_createHyperMetro_info_errSource_msg1'),

        vstoreLabel: this.get('common.owningVstore_lab'),
        rmVstoreLabel: this.get('common.remoteVstore_lab'),

        // 远端资源名称
        remoteResourceName: this.i18n.get('protection.replication_term_remoteResourceName_label'),
        // 本端资源名称
        localResourceName: this.i18n.get('protection.replication_term_localResourceName_label'),
        // 远端LUN组和远端保护组均采用该命名规则
        remotePGName: this.i18n.get('protection.replication_term_remotePGName_label'),
        sameAsLocalValue: this.i18n.get('protection.replication_term_sameAsLocal_value'),
        customValue: this.i18n.get('common.custom_value'),
        prefixMsg: this.i18n.get('protection.replication_term_prefix_validation'),
        suffixMsg: this.i18n.get('protection.replication_term_suffix_validation'),
        prefixTip: this.get('protection.replication_term_prefix_tip'),
        suffixTip: this.get('protection.replication_term_suffix_tip'),
        // 自定义（{0}
        customShowInfo1: (namePrefix): string => this.get('protection.replication_term_customShow_info1', [namePrefix]),
        // *本端资源名称*
        customShowInfo2: this.get('protection.replication_term_customShow_info2'),
        // {1}）
        customShowInfo3: (nameSuffix): string => this.get('protection.replication_term_customShow_info3', [nameSuffix]),
    };

    syn = {
        synTitle: this.get('protection.hyperMetro_task_syncHyperMetroPair_title'),
        synWarnTip: this.get('protection.dataProctection_activeToActive_task_create_pair_info_synchronize_msg'),
    };


    detail = {
        synDirection: this.get('protection.hyperMetroCG_term_replicationDierction_column'),
        recoveryPolicy: this.get('protection.replicationCG_term_rescoveryStrategy_lab'),
        recoveryPolicyTip: this.get('protection.hyperMetroCG_task_create_rescoveryStrategy_tip'),
        // 隔离开关
        isolationSwitch: this.get('protection.hyperMetroCG_view_topoParameter_isolateSwitch_label'),
        //  隔离阈值（毫秒）
        isolationthResholdTime: this.get('protection.hyperMetroCG_view_isolateThresholdForMS_label'),
        // 隔离开关描述
        isolationSwitchDescription: this.get('protection.hyperMetroCG_view_topoParameter_isolateSwitch_enable_tip'),
        speed: this.create.speed,
        speedTip: this.get('protection.replication_create_task_speed_tip'),
        localPG: this.get('protection.replicationCG_term_localPG_lab'),
        remotePG: this.get('protection.replicationCG_term_remotePG_lab'),
        localResourceRole: this.get('protection.hyperMetroCG_term_localSourceRole_column'),

        linkStatus: this.get('host.host_view_toplogy_pathDetails_linkStatus_lab'),
        owningHyperMoterDomain: this.get('protection.hyperMetroCG_term_belongDomain_column'),
        localResStatu: this.get('protection.hyperMetroCG_view_localDataStatus_column'),
        remoteResStatu: this.get('protection.hyperMetroCG_view_remoteDataStatus_column'),
        localResHostStatu: this.get('protection.hyperMetroCG_view_localResourceHostStatus_column'),
        remoteResHostStatu: this.get('protection.hyperMetroCG_view_remoteResourceHostStatus_column'),
        modifyTitle: this.get('protection.hyperMetro_task_modifyPair_title'),
        belongHyperMetroCG: this.get('protection.hyperMetroCG_term_belongHyperMetroCG_column'),
        siteArbitration: this.get('protection.hyperMetroCG_term_arbitPolicy_col'),
        syncStartTime: this.get('protection.replicationCG_term_syncStartTime_label'),
        syncEndTime: this.get('protection.replicationCG_term_syncEndTime_label'),
        syncLeftTime: this.get('protection.hyperMetro_term_syncRestTime_column'),
        metroCgLabel: this.get('protection.hyperMetroCG_title'),
        isolateSwitch_true: this.get('protection.hyperMetroCG_view_topoParameter_isolateSwitch_true_value'),
        isolateSwitch_false: this.get('protection.hyperMetroCG_view_topoParameter_isolateSwitch_false_value'),
        enableValue: this.get('common.service_enable_value'),
        disableValue: this.get('common.service_disable_value'),
    };

    pause = {
        title: this.get('protection.hyperMetro_task_pauseHyperMetroPair_title'),
        content: this.get('protection.hyperMetroCG_task_pause_selectSite_checkbox'),
        pauseTip: this.get('protection.dataProctection_activeToActive_task_create_pair_info_pause_msg'),
        preferred: this.get('common.EnumDefine_HA_SITE_GROUP_TYPE_E_PREFERED'),
        nonPreferred: this.get('common.EnumDefine_HA_SITE_GROUP_TYPE_E_NON_PREFERED'),
    };

    siteArbitration = {
        buttonTitle: this.get('protection.hyperMetroCG_term_setArbitPolicy_button'),
        title: this.get('protection.hyperMetroCG_term_arbitPolicy_col'),
        content: this.get('protection.hyperMetro_term_setArbitPolicy_comfirm_msg'),
        setArbitPolicyTip: this.get('protection.hyperMetroCG_term_setArbitPolicy_info'),
        userDefined : this.get('protection.hyperMetroCG_term_setArbitPolicy_userDefined_value'),
        setIo: this.get('protection.hyperMetroCG_term_setArbitPolicy_IO_value'),
        arbitPolicyTip: this.get('protection.hyperMetroCG_term_arbitPolicy_tip'),
    };

    forceStart = {
        title: this.get('protection.hyperMetro_task_forceStartHyperMetroPair_title'),
        tips: this.get('protection.dataProctection_activeToActive_task_create_pair_info_forceStart_msg'),
    };

    prioritySwitch = {
        title: this.get('protection.hyperMetro_task_switchHyperMetroPair_title'),
        tips: this.get('protection.dataProctection_activeToActive_task_create_pair_info_switchPriority_msg'),
    };

    delete = {
        title: this.get('protection.hyperMetro_task_deleteStartHyperMetroPair_title'),
        checkContent: this.get('protection.hyperMetro_task_delete_pair_para_onlyDelOwningDevice_checkbox'),
        refreshWWNCheckContent: this.get('protection.hyperMetro_task_delete_pair_para_flashDeviceWWN_checkbox'),
        refreshWWNCheckTip: this.get('protection.hyperMetro_task_delete_pair_para_flashDeviceWWN_desc'),
        tip: this.get('protection.hyperMetro_task_deleteHyperMetroPair_refreshWWN_msg'),
    };

    // 同步方向
    directionObj = {
        0: '--',
        1: this.get('common.EnumDefine_HC_SYNC_DIRECTION_HC_SYNC_LOCAL_TO_REMOTE'),
        2: this.get('common.EnumDefine_HC_SYNC_DIRECTION_HC_SYNC_REMOTE_TO_LOCAL'),
    };

    // 恢复策略
    recoveryPolicyObj = {
        1: this.common.automatic,
        2: this.common.manually,
    };

    // 业务保障策略
    serviceAssPolicyObj = {
        true: this.common.servicePrefValue,
        false: this.common.dataPrefValue,
    };

    // 初始同步策略
    initSyncObj = {
        1: this.common.automatic,
        2: this.common.manually,
        3: this.get('common.nosync_label'),
    };

    // 初始同步数据不一致选项
    inconsistentObj = {
        1: this.get('protection.hyperMetroCG_task_create_initialSync_dataInConsistent_automaticSync_value'),
        2: this.get('protection.hyperMetroCG_task_create_initialSync_dataInConsistent_manualSync_value'),
    };

    // 速率
    speedObj = {
        [SpeedTypeE.Low]: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_LOW'),
        [SpeedTypeE.Middle]: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_MIDDLE'),
        [SpeedTypeE.High]: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_HIGH'),
        [SpeedTypeE.Asap]: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_ASAP'),
        [SpeedTypeE.Custom]: this.get('common.custom_label'),
    };

    // 仲裁类型
    cpTypeObj = {
        1: this.get('common.EnumDefine_HC_CP_TYPE_HC_CP_SERVER'),
        2: this.get('common.EnumDefine_HC_CP_TYPE_HC_CP_DISK'),
        3: this.get('common.EnumDefine_HC_CP_TYPE_HC_CP_NONE'),
    };

    healthStatusObj = {
        1: this.get('common.EnumDefine_STATUS_E_NORMAL'),
        2: this.get('common.EnumDefine_HEALTH_STATUS_fault'),
    };

    runningStatusObj = {
        1: this.get('common.EnumDefine_STATUS_E_NORMAL'),
        23: this.get('common.EnumDefine_RUNNING_STATUS_E_SYNCHRONIZING'),
        35: this.get('common.EnumDefine_RUNNING_STATUS_E_INVALID'),
        41: this.get('common.EnumDefine_RUNNING_STATUS_E_PAUSE'),
        93: this.get('common.EnumDefine_RUNNING_STATUS_E_FORCED_START'),
        100: this.get('common.EnumDefine_RUNNING_STATUS_E_TO_BE_SYNCHRONIZED'),
    };

    // 优先站点类型
    priorityStationTypeObj = {
        true: this.get('common.EnumDefine_HA_SITE_GROUP_TYPE_E_PREFERED'),
        false: this.get('common.EnumDefine_HA_SITE_GROUP_TYPE_E_NON_PREFERED'),
    };

    initialSyncObj = {
        1: this.get('protection.hyperMetroCG_task_create_initialSync_autosync_checkbox'),
        2: this.get('protection.hyperMetroCG_task_create_initialSync_manualsync_checkbox'),
        3: this.get('protection.hyperMetroCG_task_create_initialSync_notSync_checkbox'),
    };

    yesNoObj = {
        true: this.get('common.term_yes_label'),
        false: this.get('common.term_No_label'),
    };

    linkStatus = {
        1: this.get('common.EnumDefine_RUNNING_STATUS_E_LINK_UP'),
        2: this.get('common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN'),
    };

    dataState = {
        1: this.get('common.EnumDefine_HC_DATA_STATE_HC_DATA_INTEGRITY'),
        2: this.get('common.EnumDefine_HC_DATA_STATE_HC_DATA_INCOMPLETENESS'),
    };

    hostAccessState = {
        1: this.get('protection.replicationCG_view_secondaryLunDataStatus_NoAccess_lab'),
        2: this.get('settings.userAndRole_term_readonly_list'),
        3: this.get('protection.hyperMetroCG_view_localResourceHostStatus_readAndWrite_value'),
        5: UNPLACEHOLDER,
    };

    statusClass = {
        1: 'green',
        2: 'red',
        14: 'gray',
    };

    inconsistentOpts: SelectOption[] = this.transformOpts(this.inconsistentObj);

    speedOpts: SelectOption[] = this.transformOpts(this.speedObj);

    // 策略下拉列表
    policyOpts: SelectOption[] = this.transformOpts(this.recoveryPolicyObj);

    initSyncOpts: SelectOption[] = this.transformOpts(this.initSyncObj);

    hostTypeOpts: SelectOption[] = [
        {
            label: this.common.hostGroup,
            value: 'hostGroup',
        },
        {
            label: this.common.host,
            value: 'host',
        },
    ];

    preferredArrayOpts: SelectOption[] = [
        {
            label: this.create.local,
            value: 'local',
        },
        {
            label: this.create.remote,
            value: 'remote',
        },
    ];

    /**
     *  仲裁优先站点策略
     *  1:用户自定义
     *  2:根据业务I/O判断
     */
    siteArbitrationOpts: SelectOption[] = [
        {
            label: this.siteArbitration.userDefined,
            value: SiteArbitrationTypeE.UserDefined,
        },
        {
            label: this.siteArbitration.setIo,
            value: SiteArbitrationTypeE.SetIo,
        },
    ];

    get(key, params?, colon?): string {
        return this.i18n.get(key, params, colon);
    }

    getLabel(value: string, opts: SelectOption[]): string {
        if (!value || value === '-1') {
            return '--';
        }
        const obj = opts.find((opt) => opt.value === value);
        return obj ? obj.label : '--';
    }

    poolFreeCapacity(value): string {
        return this.i18n.get('storage.lun_task_create_availableCapacity_lab', [
            this.util.adaptUnit(value || 0, Consts.SECTOR).label,
        ]);
    }

    transformOpts(obj): {
        value: string;
        label: any;
    }[] {
        return Object.keys(obj).map((key) => {
            const item = {
                value: key,
                label: obj[key],
            };
            return item;
        });
    }

    // 转换UTC时间戳为带时区信息的字符串
    renderTime = (time: number): string => time === 0 ?
        '--' :
        this.time.formatOffsetUTC(time * 1e3);

    /**
     * 生成pair名称 xxx -> xxx
     * @param pair 双活pair
     */
    getPairName(pair: HyperMetroPair): string {
        return pair.LOCALOBJNAME + this.create.pairArrowRight + pair.REMOTEOBJNAME;
    }

    formatSeconds(oriValue: number): string {
        let value = oriValue;
        value = Number(value);
        if (value === -1 || isNaN(value) || value === INVALID_NUM) {
            return UNPLACEHOLDER;
        }

        const sec = (value % 60).toString().padStart(2, '0');
        const min = (Math.floor(value / 60) % 60).toString().padStart(2, '0');
        const hour = (Math.floor(value / 3600)).toString().padStart(2, '0');

        return `${hour}:${min}:${sec}`;
    }
}
